import { useState, useCallback, useEffect } from 'react'

const STORAGE_KEY = 'mockTime'
const ENABLED_CACHE_KEY = 'mockTimeEnabled'

// Runtime cache — starts from sessionStorage if available, updated by API check
let _enabledCache: boolean | null = (() => {
  try {
    const cached = sessionStorage.getItem(ENABLED_CACHE_KEY)
    return cached === 'true' ? true : cached === 'false' ? false : null
  } catch {
    return null
  }
})()

// Check the API once per session to determine if mock time is enabled
let _enabledPromise: Promise<boolean> | null = null

function checkMockTimeEnabled(): Promise<boolean> {
  if (_enabledPromise) return _enabledPromise
  _enabledPromise = fetch('/api/dev/time', { credentials: 'include' })
    .then((res) => {
      const enabled = res.ok
      _enabledCache = enabled
      try {
        sessionStorage.setItem(ENABLED_CACHE_KEY, String(enabled))
      } catch {
        // sessionStorage unavailable
      }
      return enabled
    })
    .catch(() => {
      _enabledCache = false
      return false
    })
  return _enabledPromise
}

function readStoredDate(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredDate(value: string | null) {
  try {
    if (value) {
      localStorage.setItem(STORAGE_KEY, value)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // localStorage unavailable
  }
}

/**
 * Returns the current mock time override (ISO date string) or null.
 * Works outside of React — call anytime to read the current override for fetch headers.
 */
export function getMockTimeOverride(): string | null {
  if (_enabledCache !== true) return null
  return readStoredDate()
}

/**
 * Hook for managing mock time override state.
 * Checks the API at runtime to determine if mock time is enabled.
 * Persists to localStorage under the `mockTime` key.
 */
export function useMockTime() {
  const [enabled, setEnabled] = useState<boolean>(_enabledCache ?? false)
  const [override, setOverrideState] = useState<string | null>(() =>
    _enabledCache ? readStoredDate() : null,
  )

  useEffect(() => {
    checkMockTimeEnabled().then((result) => {
      setEnabled(result)
      if (result) {
        setOverrideState(readStoredDate())
      }
    })
  }, [])

  // Sync across tabs
  useEffect(() => {
    if (!enabled) return

    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) {
        setOverrideState(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [enabled])

  const setOverride = useCallback((dateStr: string) => {
    writeStoredDate(dateStr)
    setOverrideState(dateStr)
  }, [])

  const clearOverride = useCallback(() => {
    writeStoredDate(null)
    setOverrideState(null)
  }, [])

  return {
    /** Whether mock time feature is enabled at all */
    enabled,
    /** Current date override (ISO date string) or null */
    override,
    /** Set the mock date override */
    setOverride,
    /** Clear the mock date override */
    clearOverride,
  } as const
}
